import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  useParams,
  useSearchParams,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { useUserHasAccess } from "../../../routes/roleOnly";
import { ProjectPlanSidebar } from "../../../components/dashboard/projects/projectPlanSidebar";
import { ProjectPlanToolbar } from "../../../components/dashboard/projects/ProjectPlanToolbar";
import { PlanningQuickViewTabs } from "../../../components/dashboard/calendar/PlanningQuickViewTabs";
import { ProjectPlanCrewPanel } from "../../../components/dashboard/projects/ProjectPlanCrewPanel";
import "../../../components/dashboard/projects/projectPlanSitelane.css";
import { ProjectEventDialog } from "../../../components/dashboard/projects/projectEventDialog";
import PlanScheduleModal from "../../../components/dashboard/workOrders/plan-schedule-modal/PlanScheduleModal";
import { PlanActivityPickerDialog } from "../../../components/dashboard/projects/PlanActivityPickerDialog";
import { PlanDayActionDialog } from "../../../components/dashboard/projects/PlanDayActionDialog";
import { PlanAbsenceDialog } from "../../../components/dashboard/projects/PlanAbsenceDialog";
import { PlanResourceDayDialog } from "../../../components/dashboard/projects/PlanResourceDayDialog";
import { ProjectPickerSheet } from "../../../components/dashboard/hours/hourFormPickers";
import { RecordCollaborationPanel } from "../../../collaboration/RecordCollaborationPanel";
import { ProjectPlanScheduleView } from "../../../components/dashboard/projects/projectPlanScheduleView";
import axios from "../../../api/axios";
import { fetchHoursResourcePlan } from "../../../api/hour";
import { addDays, format, eachDayOfInterval } from "date-fns";
import { PlusIcon } from "@heroicons/react/24/outline";
import {
  updateProjectEvent,
  parseEventLinks,
  buildProjectPlanStats,
  filterEventsByUserIds,
  isDraftPlan,
} from "../../../api/projectPlan";
import {
  resolvePlanVisibleRange,
  shiftPlanAnchor,
  todayPlanAnchor,
  buildAbsencesByDate,
  buildHolidaysByDate,
  employmentEndedBeforePeriod,
  eventBoundsFromResources,
  isSickLeaveRow,
} from "../../../components/dashboard/projects/projectPlanUtils";

// Example data for development/testing
const EXAMPLE_PROJECT_EVENTS = {
  data: [
    {
      id: 1,
      title: "Installation Phase 1",
      description: "Initial installation of equipment at customer site",
      start_date: "2024-01-15",
      end_date: "2024-01-20",
      project_id: 101,
      work_order_id: 501,
      activity_id: null,
      created_at: "2024-01-10T10:00:00Z",
      updated_at: "2024-01-10T10:00:00Z",
      project: {
        id: 101,
        displayTitle: "101 - Customer Installation Project",
        name: "Customer Installation Project",
        project_number: 101
      },
      work_order: {
        id: 501,
        displayTitle: "501 - Equipment Installation",
        title: "Equipment Installation",
        number: 501,
        status: "ongoing"
      },
      activity: null,
      resources: [
        {
          id: 201,
          user_id: 10,
          user: {
            id: 10,
            displayTitle: "John Doe",
            name: "John Doe"
          },
          start_time: "2024-01-15T08:00:00Z",
          end_time: "2024-01-15T16:00:00Z",
          title: "Installation Phase 1",
          description: "Initial installation of equipment at customer site",
          project_id: 101,
          status: "pending",
          active: true
        },
        {
          id: 202,
          user_id: 11,
          user: {
            id: 11,
            displayTitle: "Jane Smith",
            name: "Jane Smith"
          },
          start_time: "2024-01-16T08:00:00Z",
          end_time: "2024-01-16T16:00:00Z",
          title: "Installation Phase 1",
          description: "Initial installation of equipment at customer site",
          project_id: 101,
          status: "pending",
          active: true
        }
      ]
    },
    {
      id: 2,
      title: "Maintenance Check",
      description: "Regular maintenance check for installed equipment",
      start_date: "2024-01-18",
      end_date: "2024-01-18",
      project_id: 101,
      work_order_id: null,
      activity_id: 301,
      created_at: "2024-01-12T14:30:00Z",
      updated_at: "2024-01-12T14:30:00Z",
      project: {
        id: 101,
        displayTitle: "101 - Customer Installation Project",
        name: "Customer Installation Project",
        project_number: 101
      },
      work_order: null,
      activity: {
        id: 301,
        name: "Maintenance",
        title: "Maintenance",
        number: "M-001"
      },
      resources: [
        {
          id: 204,
          user_id: 12,
          user: {
            id: 12,
            displayTitle: "Bob Johnson",
            name: "Bob Johnson"
          },
          start_time: "2024-01-18T09:00:00Z",
          end_time: "2024-01-18T15:00:00Z",
          title: "Maintenance Check",
          description: "Regular maintenance check for installed equipment",
          project_id: 101,
          status: "pending",
          active: true
        }
      ]
    },
    {
      id: 3,
      title: "Site Survey",
      description: "Initial site survey for new project",
      start_date: "2024-01-20",
      end_date: "2024-01-22",
      project_id: 102,
      work_order_id: null,
      activity_id: null,
      project_activity_id: 9001,
      created_at: "2024-01-08T09:15:00Z",
      updated_at: "2024-01-08T09:15:00Z",
      project: {
        id: 102,
        displayTitle: "102 - New Office Building",
        name: "New Office Building",
        project_number: 102
      },
      work_order: null,
      activity: null,
      project_activity: {
        id: 9001,
        display_title: "Site preparation",
        title: "Site preparation",
      },
      resources: [
        {
          id: 205,
          user_id: 10,
          user: {
            id: 10,
            displayTitle: "John Doe",
            name: "John Doe"
          },
          start_time: "2024-01-20T08:00:00Z",
          end_time: "2024-01-20T16:00:00Z",
          title: "Site Survey",
          description: "Initial site survey for new project",
          project_id: 102,
          status: "pending",
          active: true
        },
        {
          id: 206,
          user_id: 13,
          user: {
            id: 13,
            displayTitle: "Alice Williams",
            name: "Alice Williams"
          },
          start_time: "2024-01-21T08:00:00Z",
          end_time: "2024-01-21T16:00:00Z",
          title: "Site Survey",
          description: "Initial site survey for new project",
          project_id: 102,
          status: "pending",
          active: true
        }
      ]
    },
    {
      id: 4,
      title: "Equipment Testing",
      description: "Testing phase for new equipment",
      start_date: "2024-01-25",
      end_date: "2024-01-26",
      project_id: 101,
      work_order_id: 502,
      activity_id: 302,
      created_at: "2024-01-14T11:20:00Z",
      updated_at: "2024-01-14T11:20:00Z",
      project: {
        id: 101,
        displayTitle: "101 - Customer Installation Project",
        name: "Customer Installation Project",
        project_number: 101
      },
      work_order: {
        id: 502,
        displayTitle: "502 - Equipment Testing",
        title: "Equipment Testing",
        number: 502,
        status: "draft"
      },
      activity: {
        id: 302,
        name: "Testing",
        title: "Testing",
        number: "T-001"
      },
      resources: [
        {
          id: 208,
          user_id: 11,
          user: {
            id: 11,
            displayTitle: "Jane Smith",
            name: "Jane Smith"
          },
          start_time: "2024-01-25T08:00:00Z",
          end_time: "2024-01-25T16:00:00Z",
          title: "Equipment Testing",
          description: "Testing phase for new equipment",
          project_id: 101,
          status: "pending",
          active: true
        }
      ]
    }
  ],
  meta: {
    total: 4,
    page: 1,
    per_page: 50,
    total_pages: 1
  }
};

// Set to true to use example data instead of API calls
// Change this to true to see example data displayed
const USE_EXAMPLE_DATA = false; // Set to false to use real API

const hoursBetween = (start, end) => {
  if (!start || !end || end <= start) return 0;
  return Math.max(0, (end.getTime() - start.getTime()) / (1000 * 60 * 60));
};

const resourceHoursForDay = (resource, dayKey) => {
  const start = resource?.start_time ? new Date(resource.start_time) : null;
  const end = resource?.end_time ? new Date(resource.end_time) : null;
  if (!start) return 0;

  const startKey = format(start, "yyyy-MM-dd");
  const endKey = end ? format(end, "yyyy-MM-dd") : startKey;
  if (dayKey < startKey || dayKey > endKey) return 0;

  if (end && startKey === endKey) return hoursBetween(start, end);
  return 8;
};

/** Normalize create/update payloads from PlanScheduleModal / ProjectEventDialog. */
const unwrapSavedProjectEvent = (payload) => {
  if (!payload || typeof payload !== "object") return null;
  if (payload.project_event?.id) return payload.project_event;
  if (payload.data?.id) return payload.data;
  if (payload.data?.project_event?.id) return payload.data.project_event;
  if (payload.id) return payload;
  return null;
};

const eventOverlapsVisibleRange = (event, rangeFrom, rangeTo) => {
  if (!rangeFrom || !rangeTo) return true;
  const fromDate = new Date(rangeFrom);
  const toDate = new Date(rangeTo);
  const eventStart = event.start_date ? new Date(event.start_date) : null;
  const eventEnd = event.end_date ? new Date(event.end_date) : eventStart;
  if (eventStart && eventEnd) {
    return eventStart <= toDate && eventEnd >= fromDate;
  }
  if (eventStart) {
    return eventStart >= fromDate && eventStart <= toDate;
  }
  // Drafts without dates belong in the sidebar, not the calendar grid.
  return false;
};

const overviewEventSlice = (event) => ({
  id: event.id,
  project_id: event.project_id,
  resources: event.resources || [],
  status: event.status,
});

const ProjectPlanContent = () => {
  const { t } = useTranslation();
  const companyId = useSelector((state) => state.session.currentCompany?.id);
  const { project_id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialPeriod = searchParams.get("period") || "fortnight";
  const initialAnchor =
    searchParams.get("date") || format(new Date(), "yyyy-MM-dd");
  const initialRange = resolvePlanVisibleRange(initialAnchor, initialPeriod);

  const [selectedProject, setSelectedProject] = useState(null);
  const [periodType, setPeriodType] = useState(initialPeriod);
  const [baseDate, setBaseDate] = useState(initialRange.baseDate);
  const [dateFrom, setDateFrom] = useState(initialRange.dateFrom);
  const [dateTo, setDateTo] = useState(initialRange.dateTo);
  const [events, setEvents] = useState([]);
  const [draftPlans, setDraftPlans] = useState([]);
  const [draftsLoading, setDraftsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resourcePlan, setResourcePlan] = useState(null);
  const [resourcePlanLoading, setResourcePlanLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("full");
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [prefillDate, setPrefillDate] = useState(null);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [scheduleModalPlan, setScheduleModalPlan] = useState(null);
  const [scheduleModalWorkOrder, setScheduleModalWorkOrder] = useState(null);
  const [scheduleModalActivity, setScheduleModalActivity] = useState(null);
  const [scheduleModalMode, setScheduleModalMode] = useState("project");
  const [scheduleModalInitialDate, setScheduleModalInitialDate] = useState(null);
  const [scheduleModalPreselectDate, setScheduleModalPreselectDate] =
    useState(null);
  const [activityPickerOpen, setActivityPickerOpen] = useState(false);
  const [activityPickerInitialDate, setActivityPickerInitialDate] = useState(null);
  const [projectPickerOpen, setProjectPickerOpen] = useState(false);
  const pendingProjectActionRef = useRef(null);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [overviewScheduled, setOverviewScheduled] = useState([]);
  const [overviewDrafts, setOverviewDrafts] = useState([]);
  const [dragState, setDragState] = useState(null);
  const [dragGhost, setDragGhost] = useState(null);

  useEffect(() => {
    if (!dragState) {
      setDragGhost(null);
      return undefined;
    }
    const onMove = (e) => {
      const x = e.clientX || e.touches?.[0]?.clientX || 0;
      const y = e.clientY || e.touches?.[0]?.clientY || 0;
      setDragGhost({ x, y });
    };
    document.addEventListener("dragover", onMove);
    return () => document.removeEventListener("dragover", onMove);
  }, [dragState]);

  // Drag-to-scroll state
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [scrollStart, setScrollStart] = useState({ x: 0, y: 0 });
  const scrollContainerRef = useRef(null);
  
  // Right-click context menu state
  const [contextMenu, setContextMenu] = useState(null);

  const canAddAbsence = useUserHasAccess({ role: "admin" });
  const [dayActionOpen, setDayActionOpen] = useState(false);
  const [dayActionDate, setDayActionDate] = useState(null);
  const [absenceDialogOpen, setAbsenceDialogOpen] = useState(false);
  const [absenceInitialDate, setAbsenceInitialDate] = useState(null);
  const [resourceDayDialog, setResourceDayDialog] = useState(null);

  // Drag-to-scroll handlers
  const handleMouseDown = useCallback((e) => {
    if (e.button !== 0) return; // Only handle left mouse button
    if (!scrollContainerRef.current) return;
    
    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
    setScrollStart({
      x: scrollContainerRef.current.scrollLeft,
      y: scrollContainerRef.current.scrollTop,
    });
    e.preventDefault();
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging || !scrollContainerRef.current) return;
    
    const deltaX = e.clientX - dragStart.x;
    const deltaY = e.clientY - dragStart.y;
    
    scrollContainerRef.current.scrollLeft = scrollStart.x - deltaX;
    scrollContainerRef.current.scrollTop = scrollStart.y - deltaY;
  }, [isDragging, dragStart, scrollStart]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Right-click context menu handlers
  const handleContextMenu = useCallback((e, day, activityKey = null) => {
    e.preventDefault();
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      day: format(day, "yyyy-MM-dd"),
      activityKey,
    });
  }, []);

  const handleCloseContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  const handleAddEventFromContextMenu = useCallback(() => {
    if (contextMenu) {
      handleNewEvent(contextMenu.day);
      handleCloseContextMenu();
    }
  }, [contextMenu]);

  // Close context menu on click outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (contextMenu) {
        handleCloseContextMenu();
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [contextMenu, handleCloseContextMenu]);

  // Snap visible range to calendar week / 2 weeks / month from anchor (range start).
  useEffect(() => {
    const range = resolvePlanVisibleRange(baseDate, periodType);
    setDateFrom(range.dateFrom);
    setDateTo(range.dateTo);
    if (range.baseDate !== baseDate) {
      setBaseDate(range.baseDate);
    }
  }, [baseDate, periodType]);

  // Load project if project_id is in URL
  useEffect(() => {
    if (project_id) {
      axios({ method: "get", url: `/projects/${project_id}` })
        .then((res) => {
          if (res.data) {
            setSelectedProject(res.data);
          }
        })
        .catch((err) => {
          console.error("Error loading project:", err);
        });
    }
  }, [project_id]);

  // Fetch events. `silent` skips the full-grid loading skeleton (used after mutations).
  const fetchEvents = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      // Use example data if enabled
      if (USE_EXAMPLE_DATA) {
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Filter example data based on selected project and date range
        let filteredData = EXAMPLE_PROJECT_EVENTS.data;
        
        // Filter by project if selected
        if (selectedProject?.id) {
          filteredData = filteredData.filter(event => event.project_id === selectedProject.id);
        }
        
        // Filter by date range
        if (dateFrom && dateTo) {
          filteredData = filteredData.filter((event) =>
            eventOverlapsVisibleRange(event, dateFrom, dateTo)
          );
        }
        
        setEvents(filteredData);
        return;
      }

      // Real API call
      const params = {
        date_from: dateFrom,
        date_to: dateTo,
        fields: "*",
      };

      // Only add project_id filter if a specific project is selected
      if (selectedProject?.id) {
        params.project_id = selectedProject.id;
      }

      const response = await axios({
        method: "get",
        url: "/project_events",
        params,
      });

      if (response.data?.data) {
        setEvents(response.data.data);
      } else {
        setEvents([]);
      }
    } catch (error) {
      console.error("Error fetching project events:", error);
      // Fallback to example data only if explicitly enabled
      if (USE_EXAMPLE_DATA) {
        console.log("Using example data due to API error");
        let filteredData = EXAMPLE_PROJECT_EVENTS.data;
        if (selectedProject?.id) {
          filteredData = filteredData.filter(event => event.project_id === selectedProject.id);
        }
        setEvents(filteredData);
      } else {
        setEvents([]);
        // Show error message to user
        console.error("Failed to load project events:", error.response?.data || error.message);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [selectedProject?.id, dateFrom, dateTo]);

  const fetchPlanOverview = useCallback(async () => {
    try {
      const [scheduledRes, draftRes] = await Promise.all([
        axios.get("/project_events", {
          params: { status: "scheduled", fields: "id,project_id,resources,status", per_page: 500 },
        }),
        axios.get("/project_events", {
          params: { status: "draft", fields: "id,project_id", per_page: 500 },
        }),
      ]);
      setOverviewScheduled(scheduledRes.data?.data || []);
      setOverviewDrafts(draftRes.data?.data || []);
    } catch (error) {
      console.error("Error fetching plan overview:", error);
      setOverviewScheduled([]);
      setOverviewDrafts([]);
    }
  }, []);

  const fetchDraftPlans = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setDraftsLoading(true);
    try {
      const params = {
        status: "draft",
        fields: "*",
        per_page: 100,
        sortBy: "updated_at",
        sort: "DESC",
      };
      if (selectedProject?.id) {
        params.project_id = selectedProject.id;
      }
      const response = await axios.get("/project_events", { params });
      setDraftPlans((response.data?.data || []).filter(isDraftPlan));
    } catch (error) {
      console.error("Error fetching draft plans:", error);
      setDraftPlans([]);
    } finally {
      if (!silent) setDraftsLoading(false);
    }
  }, [selectedProject?.id]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  useEffect(() => {
    fetchDraftPlans();
  }, [fetchDraftPlans]);

  useEffect(() => {
    fetchPlanOverview();
  }, [fetchPlanOverview]);

  const fetchResourcePlan = useCallback(async ({ silent = false } = {}) => {
    if (!dateFrom || !dateTo) {
      setResourcePlan(null);
      return;
    }

    if (!silent) setResourcePlanLoading(true);
    try {
      const response = await fetchHoursResourcePlan({
        date_from: dateFrom,
        date_to: dateTo,
      });
      setResourcePlan(response?.data?.data || null);
    } catch (error) {
      console.error("Error fetching resource plan:", error);
      // Clear even on silent refresh so a failed post-mutation refetch cannot leave
      // stale crew / absence-by-date data from the previous successful fetch.
      setResourcePlan(null);
    } finally {
      if (!silent) setResourcePlanLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    fetchResourcePlan();
  }, [fetchResourcePlan]);

  const resourcePlanRefreshTimerRef = useRef(null);
  const scheduleResourcePlanRefresh = useCallback(() => {
    if (resourcePlanRefreshTimerRef.current) {
      clearTimeout(resourcePlanRefreshTimerRef.current);
    }
    resourcePlanRefreshTimerRef.current = setTimeout(() => {
      resourcePlanRefreshTimerRef.current = null;
      fetchResourcePlan({ silent: true });
    }, 350);
  }, [fetchResourcePlan]);

  useEffect(
    () => () => {
      if (resourcePlanRefreshTimerRef.current) {
        clearTimeout(resourcePlanRefreshTimerRef.current);
      }
    },
    []
  );

  const applySavedEventLocally = useCallback(
    (savedPayload) => {
      const saved = unwrapSavedProjectEvent(savedPayload);
      if (!saved?.id) return false;

      const matchesProject =
        !selectedProject?.id ||
        Number(saved.project_id) === Number(selectedProject.id);
      const draft = isDraftPlan(saved);
      const inRange = eventOverlapsVisibleRange(saved, dateFrom, dateTo);

      setEvents((prev) => {
        const without = prev.filter((e) => Number(e.id) !== Number(saved.id));
        if (!matchesProject || draft || !inRange) return without;
        return [...without, saved];
      });

      setDraftPlans((prev) => {
        const without = prev.filter((e) => Number(e.id) !== Number(saved.id));
        if (!matchesProject || !draft) return without;
        return [saved, ...without];
      });

      setOverviewScheduled((prev) => {
        const without = prev.filter((e) => Number(e.id) !== Number(saved.id));
        if (draft || saved.status === "draft") return without;
        return [...without, overviewEventSlice(saved)];
      });

      setOverviewDrafts((prev) => {
        const without = prev.filter((e) => Number(e.id) !== Number(saved.id));
        if (!draft && saved.status !== "draft") return without;
        return [...without, { id: saved.id, project_id: saved.project_id }];
      });

      return true;
    },
    [selectedProject?.id, dateFrom, dateTo]
  );

  const applyPeriodRange = useCallback((anchorStr, type) => {
    const range = resolvePlanVisibleRange(anchorStr, type);
    setBaseDate(range.baseDate);
    setDateFrom(range.dateFrom);
    setDateTo(range.dateTo);
    return range;
  }, []);

  const handleToolbarPeriodType = (type) => {
    const range = applyPeriodRange(baseDate, type);
    setPeriodType(type);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("period", type);
      next.set("date", range.baseDate);
      return next;
    });
  };

  const handleBaseDateChange = (dateStr) => {
    const range = applyPeriodRange(dateStr, periodType);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("date", range.baseDate);
      next.set("period", periodType);
      return next;
    });
  };

  const handleShiftPeriod = (direction) => {
    const nextAnchor = shiftPlanAnchor(baseDate, periodType, direction);
    handleBaseDateChange(nextAnchor);
  };

  const handleGoToday = () => {
    handleBaseDateChange(todayPlanAnchor(periodType));
  };

  const openScheduleModal = useCallback(
    ({
      plan = null,
      workOrder = null,
      projectActivity = null,
      schedulingMode = "project",
      initialDate = null,
      preselectDate = null,
    } = {}) => {
      setScheduleModalPlan(plan);
      setScheduleModalWorkOrder(workOrder);
      setScheduleModalActivity(projectActivity);
      setScheduleModalMode(schedulingMode);
      setScheduleModalInitialDate(initialDate);
      setScheduleModalPreselectDate(preselectDate);
      setScheduleModalOpen(true);
    },
    []
  );

  const openProjectEventDialog = useCallback(
    ({ event = null, mode = "full", date = null } = {}) => {
      if (!selectedProject?.id) {
        // No project selected yet — ask the user to pick one instead of
        // blocking the flow with a toast, then continue after navigation
        // via location.state (route change remounts this page).
        pendingProjectActionRef.current = {
          kind: "dialog",
          mode,
          date,
          eventId: event?.id ?? null,
        };
        setProjectPickerOpen(true);
        return;
      }
      setSelectedEvent(event);
      setPrefillDate(date);
      setDialogMode(mode);
      setDialogOpen(true);
    },
    [selectedProject?.id]
  );

  const handleNewPlan = () => {
    openProjectEventDialog({ mode: "plan" });
  };

  const handleScheduleActivity = useCallback(
    (date = null) => {
      if (!selectedProject?.id) {
        pendingProjectActionRef.current = {
          kind: "schedule_activity",
          date: date || baseDate,
        };
        setProjectPickerOpen(true);
        return;
      }
      setActivityPickerInitialDate(date || baseDate);
      setActivityPickerOpen(true);
    },
    [selectedProject?.id, baseDate]
  );

  const handleScheduleActivityFromContextMenu = useCallback(() => {
    if (contextMenu) {
      handleScheduleActivity(contextMenu.day);
      handleCloseContextMenu();
    }
  }, [contextMenu, handleScheduleActivity, handleCloseContextMenu]);

  const handleActivityPicked = useCallback(
    (activity) => {
      openScheduleModal({
        projectActivity: activity,
        schedulingMode: "project_activity",
        initialDate: activityPickerInitialDate || baseDate,
      });
      setActivityPickerInitialDate(null);
    },
    [openScheduleModal, activityPickerInitialDate, baseDate]
  );

  const handleNewEvent = (date = null) => {
    openProjectEventDialog({ mode: "full", date: date || baseDate });
  };

  const handleOpenAbsenceDialog = useCallback((date = null) => {
    if (!canAddAbsence) return;
    setAbsenceInitialDate(date || null);
    setAbsenceDialogOpen(true);
  }, [canAddAbsence]);

  const handleDayAction = useCallback((dateKey) => {
    setDayActionDate(dateKey);
    setDayActionOpen(true);
  }, []);

  const handleDayActionSelect = useCallback(
    (action, dateKey) => {
      const day = dateKey || dayActionDate;
      if (action === "plan") {
        if (!selectedProject?.id) {
          pendingProjectActionRef.current = {
            kind: "schedule_plan",
            date: day,
          };
          setProjectPickerOpen(true);
          return;
        }
        openScheduleModal({
          schedulingMode: "project",
          initialDate: day,
          preselectDate: day,
        });
        return;
      }
      if (action === "activity") {
        handleScheduleActivity(day);
        return;
      }
      if (action === "task") {
        handleNewEvent(day);
        return;
      }
      if (action === "absence") {
        handleOpenAbsenceDialog(day);
      }
    },
    [
      dayActionDate,
      selectedProject?.id,
      openScheduleModal,
      handleScheduleActivity,
      handleOpenAbsenceDialog,
    ]
  );

  const handleResourceDayClick = useCallback((payload) => {
    setResourceDayDialog(payload);
  }, []);

  const handleAbsenceSaved = useCallback(() => {
    // Absences live in resource_plan; refresh quietly so the crew strip stays mounted.
    scheduleResourcePlanRefresh();
  }, [scheduleResourcePlanRefresh]);

  const handleEditEvent = (event) => {
    if (event?.status === "draft") {
      setSelectedEvent(event);
      setPrefillDate(null);
      setDialogMode("plan");
      setDialogOpen(true);
      return;
    }
    const { workOrders, activities } = parseEventLinks(event);
    const primaryWo = workOrders[0] || event?.work_order || null;
    const primaryPa = activities[0] || event?.project_activity || null;
    openScheduleModal({
      plan: event,
      workOrder: primaryWo,
      projectActivity: primaryPa,
      schedulingMode: primaryWo?.id
        ? "work_order"
        : primaryPa?.id
          ? "project_activity"
          : "project",
      initialDate: event?.start_date?.substring?.(0, 10) || baseDate,
    });
  };

  const handleEditProjectEvent = useCallback(() => {
    if (!scheduleModalPlan) return;
    setScheduleModalOpen(false);
    setSelectedEvent(scheduleModalPlan);
    setPrefillDate(null);
    setDialogMode("full");
    setDialogOpen(true);
  }, [scheduleModalPlan]);

  const handleSchedulePlan = (plan) => {
    const { workOrders, activities } = parseEventLinks(plan);
    const primaryWo = workOrders[0] || plan?.work_order || null;
    const primaryPa = activities[0] || plan?.project_activity || null;
    openScheduleModal({
      plan,
      workOrder: primaryWo,
      projectActivity: primaryPa,
      schedulingMode: primaryWo?.id
        ? "work_order"
        : primaryPa?.id
          ? "project_activity"
          : "project",
      initialDate: plan.start_date?.substring?.(0, 10) || baseDate,
    });
  };

  const handleEventSaved = useCallback(
    (savedPayload) => {
      const applied = applySavedEventLocally(savedPayload);
      if (!applied) {
        fetchEvents({ silent: true });
        fetchDraftPlans({ silent: true });
        fetchPlanOverview();
      }
      // Capacity / planned hours on the resource strip come from resource_plan.
      scheduleResourcePlanRefresh();
    },
    [
      applySavedEventLocally,
      fetchEvents,
      fetchDraftPlans,
      fetchPlanOverview,
      scheduleResourcePlanRefresh,
    ]
  );

  const handleSelectProject = useCallback(
    (project, { locationState } = {}) => {
      if (!project) {
        setSelectedProject(null);
        navigate("/dashboard/projects/plan");
        return;
      }
      setSelectedProject(project);
      navigate(`/dashboard/projects/${project.id}/plan`, {
        state: locationState || undefined,
      });
    },
    [navigate]
  );

  // Resolves the project picker opened by openProjectEventDialog /
  // handleScheduleActivity when no project was selected yet: select the
  // chosen project and pass the pending action through navigation state so it
  // survives the remount when switching /plan → /:project_id/plan.
  const handleProjectPickedForPendingAction = useCallback(
    (project) => {
      setProjectPickerOpen(false);
      const pending = pendingProjectActionRef.current;
      pendingProjectActionRef.current = null;
      handleSelectProject(project, {
        locationState: pending ? { pendingPlanAction: pending } : undefined,
      });
    },
    [handleSelectProject]
  );

  // After navigating with a pending plan action (and the project has loaded),
  // continue the original toolbar flow.
  useEffect(() => {
    const pending = location.state?.pendingPlanAction;
    if (!pending || !selectedProject?.id) return;

    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: {},
    });

    if (pending.kind === "schedule_activity") {
      setActivityPickerInitialDate(pending.date || baseDate);
      setActivityPickerOpen(true);
      return;
    }

    if (pending.kind === "schedule_plan") {
      openScheduleModal({
        schedulingMode: "project",
        initialDate: pending.date || baseDate,
        preselectDate: pending.date || baseDate,
      });
      return;
    }

    if (pending.kind === "dialog") {
      setSelectedEvent(null);
      setPrefillDate(pending.date || null);
      setDialogMode(pending.mode || "full");
      setDialogOpen(true);
    }
  }, [
    location.state?.pendingPlanAction,
    location.pathname,
    location.search,
    selectedProject?.id,
    baseDate,
    navigate,
    openScheduleModal,
  ]);

  const handleToggleUser = useCallback((userId) => {
    setSelectedUserIds((prev) => {
      const key = String(userId);
      if (prev.some((id) => String(id) === key)) {
        return prev.filter((id) => String(id) !== key);
      }
      return [...prev, userId];
    });
  }, []);

  const planStatsByProject = useMemo(
    () => buildProjectPlanStats(overviewScheduled, overviewDrafts),
    [overviewScheduled, overviewDrafts]
  );

  const displayEvents = useMemo(
    () => filterEventsByUserIds(events, selectedUserIds),
    [events, selectedUserIds]
  );

  const mapResourcesPayload = (resources = []) =>
    resources
      .filter((r) => r.user_id && r.start_time)
      .map((r) => ({
        id: r.id || null,
        user_id: Number(r.user_id),
        start_time: r.start_time,
        end_time: r.end_time || r.start_time,
      }));

  const handleResourceDayEdit = (event) => {
    if (event) handleEditEvent(event);
  };

  const handleResourceDayDelete = useCallback(
    async (event, resource, dayKey) => {
      if (!event?.id || !dayKey) return;
      const day = String(dayKey).substring(0, 10);
      const resourceId = resource?.id;
      const userId = resource?.user_id;
      const nextResources = (event.resources || []).filter((r) => {
        const rDay = r.start_time?.substring?.(0, 10) || "";
        if (resourceId && r.id) {
          return !(Number(r.id) === Number(resourceId) && rDay === day);
        }
        return !(String(r.user_id) === String(userId) && rDay === day);
      });
      const emptied = nextResources.length === 0;
      const bounds = eventBoundsFromResources(nextResources);
      // Last assignment removed → return to draft (no calendar bar / stale dates).
      const optimistic = emptied
        ? {
            ...event,
            start_date: null,
            end_date: null,
            status: "draft",
            resources: [],
          }
        : {
            ...event,
            start_date: bounds.start_date || event.start_date,
            end_date: bounds.end_date || event.end_date || bounds.start_date,
            resources: nextResources,
          };
      applySavedEventLocally(optimistic);
      try {
        const payload = {
          start_date: optimistic.start_date,
          end_date: optimistic.end_date,
          resources: mapResourcesPayload(nextResources),
        };
        if (emptied) payload.status = "draft";
        const res = await updateProjectEvent(event.id, payload);
        toast.success(t("project.plan.resource_day.delete_toast"));
        const applied = applySavedEventLocally(res?.data || optimistic);
        if (!applied) {
          fetchEvents({ silent: true });
        }
        scheduleResourcePlanRefresh();
      } catch {
        toast.error(t("project.plan.resource_day.delete_error"));
        // Roll back optimistic draft/overview move from applySavedEventLocally
        // above — fetchEvents alone would leave sidebar drafts/counts wrong.
        applySavedEventLocally(event);
        fetchEvents({ silent: true });
      }
    },
    [applySavedEventLocally, fetchEvents, scheduleResourcePlanRefresh, t]
  );

  const handleEventDatesChange = useCallback(
    async (event, { start_date, end_date, resources }) => {
      const nextResources = mapResourcesPayload(resources || event.resources);
      const bounds = eventBoundsFromResources(nextResources.length ? nextResources : resources || []);
      const nextStart = start_date ?? bounds.start_date ?? event.start_date;
      const nextEnd = end_date ?? bounds.end_date ?? event.end_date ?? nextStart;
      setEvents((prev) =>
        prev.map((pe) =>
          pe.id === event.id
            ? {
                ...pe,
                start_date: nextStart,
                end_date: nextEnd,
                resources: resources || pe.resources,
              }
            : pe
        )
      );
      try {
        await updateProjectEvent(event.id, {
          start_date: nextStart,
          end_date: nextEnd,
          resources: nextResources,
        });
        scheduleResourcePlanRefresh();
      } catch {
        fetchEvents({ silent: true });
      }
    },
    [fetchEvents, scheduleResourcePlanRefresh]
  );

  const handleDraftDrop = useCallback(
    (draftId, dayKey) => {
      const plan = draftPlans.find((p) => Number(p.id) === Number(draftId));
      if (!plan) {
        toast.error(t("project.plan.draft_drop_missing"));
        return;
      }
      // Dropped day becomes the first selected day in the schedule modal.
      openScheduleModal({
        plan,
        workOrder: plan?.work_order || null,
        projectActivity: plan?.project_activity || null,
        schedulingMode: plan?.work_order_id
          ? "work_order"
          : plan?.project_activity_id
            ? "project_activity"
            : "project",
        initialDate: dayKey,
        preselectDate: dayKey,
      });
    },
    [draftPlans, openScheduleModal, t]
  );

  const handleAssignResource = useCallback(
    async (event, userId, dayKey) => {
      if (!event?.id || !userId) return;
      const day =
        dayKey?.substring?.(0, 10) ||
        event.start_date?.substring?.(0, 10) ||
        baseDate;
      const alreadyAssigned = (event.resources || []).some(
        (r) =>
          String(r.user_id) === String(userId) &&
          (r.start_time?.substring?.(0, 10) || "") === day
      );
      if (alreadyAssigned) return;

      const baseStart = `${day}T08:00:00`;
      const baseEnd = `${day}T16:00:00`;

      const nextResource = {
        user_id: Number(userId),
        start_time: new Date(baseStart).toISOString(),
        end_time: new Date(baseEnd).toISOString(),
      };

      const optimistic = {
        ...event,
        resources: [...(event.resources || []), { id: null, ...nextResource }],
      };
      const bounds = eventBoundsFromResources(optimistic.resources);
      optimistic.start_date = bounds.start_date || event.start_date;
      optimistic.end_date = bounds.end_date || event.end_date;
      setEvents((prev) => prev.map((pe) => (pe.id === event.id ? optimistic : pe)));

      try {
        const res = await updateProjectEvent(event.id, {
          start_date: optimistic.start_date,
          end_date: optimistic.end_date,
          resources: mapResourcesPayload(optimistic.resources),
        });
        // Prefer server payload; keep optimistic if the patch body is thin/unwrappable.
        const applied = applySavedEventLocally(res?.data || optimistic);
        const saved = unwrapSavedProjectEvent(res?.data);
        const serverResources = saved?.resources;
        // Patch responses often omit nested resources or leave id:null rows.
        // Silent refetch hydrates real resource ids (same fallback idea as handleEventSaved).
        const needsRefetch =
          !applied ||
          !Array.isArray(serverResources) ||
          serverResources.length < optimistic.resources.length ||
          serverResources.some((r) => r?.id == null);
        if (needsRefetch) {
          fetchEvents({ silent: true });
        }
        scheduleResourcePlanRefresh();
      } catch {
        fetchEvents({ silent: true });
      }
    },
    [applySavedEventLocally, baseDate, fetchEvents, scheduleResourcePlanRefresh]
  );

  const resourcePlanByDate = useMemo(() => {
    const map = new Map();
    (resourcePlan?.days || []).forEach((day) => {
      map.set(day.date, day);
    });
    return map;
  }, [resourcePlan]);

  const absencesByDate = useMemo(
    () => buildAbsencesByDate(resourcePlan),
    [resourcePlan]
  );

  const holidaysByDate = useMemo(
    () => buildHolidaysByDate(resourcePlan),
    [resourcePlan]
  );

  const projectCapacityByDate = useMemo(() => {
    const map = new Map();

    displayEvents.forEach((event) => {
      const eventStartKey = event.start_date ? format(new Date(event.start_date), "yyyy-MM-dd") : null;
      const eventEndKey = event.end_date ? format(new Date(event.end_date), "yyyy-MM-dd") : eventStartKey;
      if (!eventStartKey) return;

      const eventStart = new Date(eventStartKey);
      const eventEnd = new Date(eventEndKey);
      eachDayOfInterval({ start: eventStart, end: eventEnd }).forEach((day) => {
        const dKey = format(day, "yyyy-MM-dd");
        const current = map.get(dKey) || {
          events: 0,
          assignedResources: 0,
          assignedHours: 0,
        };

        current.events += 1;
        (event.resources || []).forEach((resource) => {
          const hours = resourceHoursForDay(resource, dKey);
          if (hours > 0) {
            current.assignedResources += 1;
            current.assignedHours += hours;
          }
        });

        map.set(dKey, current);
      });
    });

    return map;
  }, [displayEvents]);

  const capacityDays = useMemo(() => {
    if (!dateFrom || !dateTo) return [];

    return eachDayOfInterval({
      start: new Date(dateFrom),
      end: new Date(dateTo),
    }).map((day) => {
      const dKey = format(day, "yyyy-MM-dd");
      const resourceDay = resourcePlanByDate.get(dKey);
      const projectDay = projectCapacityByDate.get(dKey) || {};
      const available = resourceDay?.stats?.available || 0;
      const scheduledHours = (resourceDay?.users || []).reduce((sum, row) => {
        if (employmentEndedBeforePeriod(row, dateFrom)) return sum;
        if (isSickLeaveRow(row)) return sum;
        return sum + (Number(row.scheduled_hours) || 0);
      }, 0);
      const assignedResources = projectDay.assignedResources || 0;
      const assignedHours = projectDay.assignedHours || 0;

      return {
        date: dKey,
        available,
        scheduledHours,
        assignedResources,
        assignedHours,
        events: projectDay.events || 0,
        freeResources: Math.max(available - assignedResources, 0),
        freeHours: Math.max(scheduledHours - assignedHours, 0),
      };
    });
  }, [dateFrom, dateTo, projectCapacityByDate, resourcePlanByDate]);

  const capacityByDate = useMemo(() => {
    const out = {};
    capacityDays.forEach((day) => {
      out[day.date] = day;
    });
    return out;
  }, [capacityDays]);

  const capacityTotals = useMemo(
    () =>
      capacityDays.reduce(
        (acc, day) => ({
          events: acc.events + day.events,
          available: acc.available + day.available,
          assignedResources: acc.assignedResources + day.assignedResources,
          scheduledHours: acc.scheduledHours + day.scheduledHours,
          assignedHours: acc.assignedHours + day.assignedHours,
          freeHours: acc.freeHours + day.freeHours,
        }),
        {
          events: 0,
          available: 0,
          assignedResources: 0,
          scheduledHours: 0,
          assignedHours: 0,
          freeHours: 0,
        }
      ),
    [capacityDays]
  );

  const scheduleStats = useMemo(() => {
    const crewIds = new Set();
    let capacityHours = 0;
    (resourcePlan?.days || []).forEach((day) => {
      (day.users || []).forEach((row) => {
        if (employmentEndedBeforePeriod(row, resourcePlan?.date_from)) return;
        if (row.user?.id) crewIds.add(String(row.user.id));
        if (!isSickLeaveRow(row)) capacityHours += row.scheduled_hours || 0;
      });
    });
    return {
      eventsCount: displayEvents.length,
      scheduledHours: capacityTotals.assignedHours,
      capacityHours,
      crewCount: crewIds.size,
      unscheduledCount: draftPlans.length,
    };
  }, [resourcePlan, displayEvents.length, capacityTotals.assignedHours, draftPlans.length]);

  const activeProjectCount = useMemo(
    () => Object.values(planStatsByProject).filter((s) => s.isPlanned).length,
    [planStatsByProject]
  );


  // Lock page-level scrolling while this full-screen view is mounted.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const planEditing = Boolean(
    dialogOpen ||
      scheduleModalOpen ||
      activityPickerOpen ||
      dayActionOpen ||
      absenceDialogOpen ||
      resourceDayDialog
  );

  return (
    <div
      className="project-plan-sitelane flex h-[calc(100vh-4rem)] flex-col overflow-hidden bg-[#F6F8FB]"
    >
      <RecordCollaborationPanel
        record={companyId ? { id: companyId, edit_version: String(companyId) } : null}
        recordType="project_plan"
        section={planEditing ? "plan" : "overview"}
        editing={planEditing}
        onRefresh={async () => ({
          apply: () => {
            fetchEvents({ silent: true });
            fetchDraftPlans({ silent: true });
            fetchPlanOverview();
            fetchResourcePlan({ silent: true });
          },
        })}
      />
      <div className="hidden lg:flex items-center border-b border-[#E5E9F0] bg-white px-4 py-2">
        <PlanningQuickViewTabs />
      </div>
      <ProjectPlanToolbar
        periodType={periodType}
        onPeriodTypeChange={handleToolbarPeriodType}
        dateFrom={dateFrom}
        dateTo={dateTo}
        onShiftPeriod={handleShiftPeriod}
        onGoToday={handleGoToday}
        onNewPlan={handleNewPlan}
        onScheduleActivity={handleScheduleActivity}
        onNewTask={() => handleNewEvent()}
        onNewAbsence={() => handleOpenAbsenceDialog()}
        canAddAbsence={canAddAbsence}
        draftCount={draftPlans.length}
        eventsCount={scheduleStats.eventsCount}
        scheduledHours={scheduleStats.scheduledHours}
        capacityHours={scheduleStats.capacityHours}
        crewCount={scheduleStats.crewCount}
        unscheduledCount={scheduleStats.unscheduledCount}
      />

      {contextMenu && (
        <div
          className="fixed z-50 min-w-[150px] rounded-lg border border-[#E5E9F0] bg-white py-1 shadow-lg"
          style={{ left: `${contextMenu.x}px`, top: `${contextMenu.y}px` }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className="flex w-full items-center px-4 py-2 text-left text-sm text-[#364152] hover:bg-[#EEF2F7]"
            onClick={handleAddEventFromContextMenu}
          >
            <PlusIcon className="mr-2 h-4 w-4" />
            {t("project.event.create")}
          </button>
          <button
            type="button"
            className="flex w-full items-center px-4 py-2 text-left text-sm text-[#364152] hover:bg-[#EEF2F7]"
            onClick={handleScheduleActivityFromContextMenu}
          >
            <PlusIcon className="mr-2 h-4 w-4 text-violet-600" />
            {t("project.plan.toolbar.schedule_activity")}
          </button>
        </div>
      )}

      <div className="main grid min-h-0 flex-1 grid-cols-1 overflow-hidden xl:grid-cols-[280px_1fr_280px]">
        <ProjectPlanSidebar
          selectedProjectId={selectedProject?.id || project_id}
          planStatsByProject={planStatsByProject}
          onSelectProject={handleSelectProject}
          activeProjectCount={activeProjectCount}
          draftPlans={draftPlans}
          draftsLoading={draftsLoading}
          onEditDraft={handleEditEvent}
          onScheduleDraft={handleSchedulePlan}
          onDragDraftStart={(plan) => setDragState({ type: "draft", payload: plan })}
          onDragDraftEnd={() => setDragState(null)}
          className="max-h-64 xl:max-h-none"
        />

        <div className="cal flex min-w-0 flex-col overflow-hidden border-r border-[#E5E9F0]">
          <div
            ref={scrollContainerRef}
            className={`cal-scroller flex-1 overflow-auto bg-[#F6F8FB] ${isDragging ? "cursor-grabbing select-none" : ""}`}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          >
            <ProjectPlanScheduleView
              dateFrom={dateFrom}
              dateTo={dateTo}
              periodType={periodType}
              events={displayEvents}
              capacityByDate={capacityByDate}
              absencesByDate={absencesByDate}
              holidaysByDate={holidaysByDate}
              loading={loading}
              canEdit
              dragKind={dragState?.type || null}
              onEventClick={handleEditEvent}
              onNewEvent={handleNewEvent}
              onDayAction={handleDayAction}
              onResourceDayClick={handleResourceDayClick}
              onEventDatesChange={handleEventDatesChange}
              onDraftDrop={handleDraftDrop}
              onAssignResource={handleAssignResource}
            />
          </div>
        </div>

        <ProjectPlanCrewPanel
          resourcePlan={resourcePlan}
          loading={resourcePlanLoading}
          selectedUserIds={selectedUserIds}
          onToggleUser={handleToggleUser}
          onClearSelection={() => setSelectedUserIds([])}
          onDragResourceStart={(member) =>
            setDragState({ type: "resource", payload: member })
          }
          onDragResourceEnd={() => setDragState(null)}
          draggingResourceId={
            dragState?.type === "resource" ? dragState.payload?.id : null
          }
        />

        {dragState && dragGhost ? (
          <div
            className="pp-drag-ghost"
            style={{ left: dragGhost.x + 12, top: dragGhost.y }}
          >
            {dragState.type === "draft" ? (
              <>
                <PlusIcon className="h-3.5 w-3.5 text-[#2B6CFF]" />
                <span>{dragState.payload?.title}</span>
              </>
            ) : (
              <>
                <span
                  className="inline-flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-semibold text-white"
                  style={{ backgroundColor: "#2B6CFF" }}
                >
                  {(dragState.payload?.displayTitle || "?")
                    .split(" ")
                    .map((w) => w[0])
                    .filter(Boolean)
                    .slice(0, 2)
                    .join("")
                    .toUpperCase()}
                </span>
                <span>{dragState.payload?.displayTitle}</span>
              </>
            )}
          </div>
        ) : null}
      </div>

      <ProjectEventDialog
          open={dialogOpen}
          mode={dialogMode}
          onClose={() => {
            setDialogOpen(false);
            setSelectedEvent(null);
            setPrefillDate(null);
            setDialogMode("full");
          }}
          event={selectedEvent}
          project={selectedProject}
          prefillDate={prefillDate}
          onSaved={handleEventSaved}
        />

      <ProjectPickerSheet
        open={projectPickerOpen}
        onClose={() => setProjectPickerOpen(false)}
        selectedId={selectedProject?.id}
        onSelect={handleProjectPickedForPendingAction}
        title={t("project.plan.pick_project_title")}
        hint={t("project.plan.pick_project_hint")}
      />

      <PlanActivityPickerDialog
        open={activityPickerOpen}
        onClose={() => {
          setActivityPickerOpen(false);
          setActivityPickerInitialDate(null);
        }}
        project={selectedProject}
        onContinue={handleActivityPicked}
      />

      <PlanScheduleModal
        open={scheduleModalOpen}
        onClose={() => {
          setScheduleModalOpen(false);
          setScheduleModalPlan(null);
          setScheduleModalWorkOrder(null);
          setScheduleModalActivity(null);
          setScheduleModalMode("project");
          setScheduleModalInitialDate(null);
          setScheduleModalPreselectDate(null);
        }}
        project={selectedProject}
        workOrder={scheduleModalWorkOrder}
        projectActivity={scheduleModalActivity}
        schedulingMode={scheduleModalMode}
        plan={scheduleModalPlan}
        initialDate={scheduleModalInitialDate}
        preselectDate={scheduleModalPreselectDate}
        onEditProjectEvent={handleEditProjectEvent}
        onSaved={handleEventSaved}
      />

      <PlanDayActionDialog
        open={dayActionOpen}
        onClose={() => {
          setDayActionOpen(false);
          setDayActionDate(null);
        }}
        dateKey={dayActionDate}
        canAddAbsence={canAddAbsence}
        onSelect={handleDayActionSelect}
      />

      <PlanAbsenceDialog
        open={absenceDialogOpen}
        onClose={() => {
          setAbsenceDialogOpen(false);
          setAbsenceInitialDate(null);
        }}
        initialDate={absenceInitialDate}
        onSaved={handleAbsenceSaved}
      />

      <PlanResourceDayDialog
        open={Boolean(resourceDayDialog)}
        onClose={() => setResourceDayDialog(null)}
        event={resourceDayDialog?.event || null}
        resource={resourceDayDialog?.resource || null}
        dayKey={resourceDayDialog?.dayKey || null}
        employeeLabel={resourceDayDialog?.employee?.label || ""}
        onEdit={handleResourceDayEdit}
        onDelete={handleResourceDayDelete}
      />
    </div>
  );
};

export const ProjectPlan = () => {
  const canAccessProjectPlan = useUserHasAccess({ roles: ["projects_plan"] });

  if (!canAccessProjectPlan) {
    return <Navigate to="/dashboard" replace />;
  }

  return <ProjectPlanContent />;
};

